import { Volume2, VolumeX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const SOUND_KEY = "bunkerfall.soundscape";

const SOUNDSCAPE_LAYERS = [
  { id: "bunker", src: "/static/assets/audio/v1/bunker-bed-v1.wav" },
  { id: "rain", src: "/static/assets/audio/v1/surface-rain-v1.wav" },
  { id: "machinery", src: "/static/assets/audio/v1/machinery-bed-v1.wav" },
  { id: "alert", src: "/static/assets/audio/v1/horde-alert-v1.wav" },
] as const;

type SoundscapeLayerId = (typeof SOUNDSCAPE_LAYERS)[number]["id"];

interface ActiveLayer {
  gain: GainNode;
  source: AudioBufferSourceNode;
}

function savedPreference(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) === "on";
  } catch {
    return false;
  }
}

function layerVolumes(
  power: "dark" | "emergency" | "stable",
  danger: boolean,
): Record<SoundscapeLayerId, number> {
  return {
    bunker: power === "dark" ? .11 : power === "emergency" ? .17 : .14,
    rain: .065,
    machinery: power === "dark" ? .008 : power === "emergency" ? .045 : .085,
    alert: danger ? .16 : 0,
  };
}

interface SoundToggleProps {
  power: "dark" | "emergency" | "stable";
  danger: boolean;
}

export function SoundToggle({ power, danger }: SoundToggleProps) {
  const [enabled, setEnabled] = useState(savedPreference);
  const [status, setStatus] = useState<"muted" | "activation" | "loading" | "playing" | "blocked" | "unavailable">(
    enabled ? "activation" : "muted",
  );
  const contextRef = useRef<AudioContext | null>(null);
  const layersRef = useRef<Map<SoundscapeLayerId, ActiveLayer>>(new Map());
  const loadingRef = useRef(false);
  const enabledRef = useRef(enabled);
  const powerRef = useRef(power);
  const dangerRef = useRef(danger);

  enabledRef.current = enabled;
  powerRef.current = power;
  dangerRef.current = danger;

  const stopSoundscape = useCallback(() => {
    for (const layer of layersRef.current.values()) {
      try { layer.source.stop(); } catch { /* Already stopped during teardown. */ }
    }
    layersRef.current.clear();
    const context = contextRef.current;
    contextRef.current = null;
    loadingRef.current = false;
    if (context) void context.close();
  }, []);

  const startSoundscape = useCallback(async () => {
    if (!enabledRef.current || loadingRef.current) return;
    const AudioContextClass = window.AudioContext;
    if (!AudioContextClass) {
      setStatus("unavailable");
      return;
    }
    const context = contextRef.current ?? new AudioContextClass();
    contextRef.current = context;
    if (context.state !== "running") {
      try {
        await context.resume();
      } catch {
        if (enabledRef.current) setStatus("blocked");
        return;
      }
    }
    if (!enabledRef.current) return;
    if (layersRef.current.size === SOUNDSCAPE_LAYERS.length) {
      setStatus("playing");
      return;
    }

    loadingRef.current = true;
    setStatus("loading");
    try {
      const master = context.createGain();
      master.gain.value = .72;
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -24;
      compressor.knee.value = 18;
      compressor.ratio.value = 5;
      compressor.attack.value = .02;
      compressor.release.value = .65;
      master.connect(compressor).connect(context.destination);
      const decoded = await Promise.all(SOUNDSCAPE_LAYERS.map(async (definition) => {
        const response = await fetch(definition.src);
        if (!response.ok) throw new Error(`Soundscape layer failed: ${definition.src}`);
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        return { definition, buffer };
      }));
      if (!enabledRef.current || contextRef.current !== context) return;
      const volumes = layerVolumes(powerRef.current, dangerRef.current);
      for (const { definition, buffer } of decoded) {
        const source = context.createBufferSource();
        const gain = context.createGain();
        source.buffer = buffer;
        source.loop = true;
        gain.gain.value = volumes[definition.id];
        source.connect(gain).connect(master);
        source.start();
        layersRef.current.set(definition.id, { gain, source });
      }
      setStatus("playing");
    } catch {
      if (enabledRef.current) setStatus("unavailable");
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus("muted");
      stopSoundscape();
      return;
    }
    const activate = () => { void startSoundscape(); };
    window.addEventListener("pointerdown", activate);
    window.addEventListener("keydown", activate);
    return () => {
      window.removeEventListener("pointerdown", activate);
      window.removeEventListener("keydown", activate);
    };
  }, [enabled, startSoundscape, stopSoundscape]);

  useEffect(() => () => stopSoundscape(), [stopSoundscape]);

  useEffect(() => {
    const context = contextRef.current;
    if (!context || status !== "playing") return;
    const volumes = layerVolumes(power, danger);
    for (const [id, layer] of layersRef.current) {
      layer.gain.gain.cancelScheduledValues(context.currentTime);
      layer.gain.gain.setTargetAtTime(volumes[id], context.currentTime, id === "alert" ? .18 : .65);
    }
  }, [danger, power, status]);

  function toggle() {
    const next = !enabled;
    setEnabled(next);
    if (next) {
      // This call happens inside the click gesture, which is the only reliable
      // moment to resume Web Audio under browser autoplay policies.
      enabledRef.current = true;
      void startSoundscape();
    }
    try {
      window.localStorage.setItem(SOUND_KEY, next ? "on" : "off");
    } catch {
      // Sound still works for this session when storage is unavailable.
    }
  }

  const label = enabled
    ? status === "playing"
      ? "Mute layered bunker soundscape"
      : status === "blocked" || status === "activation"
        ? "Activate bunker soundscape"
        : status === "unavailable"
          ? "Bunker soundscape unavailable; mute sound preference"
          : "Mute bunker soundscape while it loads"
    : "Enable layered bunker soundscape";

  return (
    <button
      className={`sound-toggle ${enabled ? "enabled" : ""} ${status === "playing" ? "sound-ready" : ""}`}
      onClick={toggle}
      aria-pressed={enabled}
      aria-label={label}
      title={label}
      data-sound-layers={SOUNDSCAPE_LAYERS.length}
      data-sound-state={status}
    >
      {enabled ? <Volume2 aria-hidden="true" /> : <VolumeX aria-hidden="true" />}
      <span>Sound</span>
      <i aria-hidden="true"><b /><b /><b /></i>
    </button>
  );
}
